import React from 'react';
import { Flame } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StreakBadgeProps {
  streakCount: number;
  streakType?: 'giving' | 'receiving' | 'posting';
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const streakTypeLabels: Record<string, string> = {
  giving: 'Giving',
  receiving: 'Receiving', 
  posting: 'Posting'
};

export const StreakBadge: React.FC<StreakBadgeProps> = ({
  streakCount,
  streakType,
  showLabel = true,
  size = 'md',
  className
}) => {
  if (streakCount <= 0) return null;

  const isHot = streakCount >= 7;
  const isLegendary = streakCount >= 30;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-sm px-3 py-1 gap-1.5',
    lg: 'text-base px-4 py-1.5 gap-2'
  };

  const iconSizes = {
    sm: 12,
    md: 14,
    lg: 18
  };

  return (
    <div 
      className={cn(
        'inline-flex items-center rounded-full font-medium transition-all',
        sizeClasses[size],
        isLegendary 
          ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-500 border border-yellow-500/30' 
          : isHot 
            ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-500 border border-orange-500/30 animate-pulse'
            : 'bg-muted text-muted-foreground',
        isHot && 'shadow-lg shadow-orange-500/20',
        className
      )}
    >
      <Flame 
        size={iconSizes[size]} 
        className={cn(
          'flex-shrink-0',
          isLegendary ? 'text-yellow-400' : isHot ? 'text-orange-400 animate-pulse' : ''
        )} 
      />
      <span className="font-bold">{streakCount}</span>
      {showLabel && (
        <span className="opacity-80">
          day{streakCount !== 1 ? 's' : ''} {streakType ? streakTypeLabels[streakType] : 'streak'}
        </span>
      )}
    </div>
  );
};
